import React, { useState, useCallback, useMemo } from 'react';
import { useSelector, useDispatch, shallowEqual } from 'react-redux';
import _ from 'lodash';
import ButtonWithIcon from '../../inputs/ListWithIcon';
import TimeDashboard from './TimeDashboard.js';
import SupportContact from './SupportContactModal';
import NotificationBell from './NotificationBell';
import Logo from '../../assets/images/ipacket_logo.png';
import {
    setDealership,
    resetRefresh,
    resetNav,
    resetAdmin,
    resetSite,
    setCurrentReportTab,
    setReportWorkFlows,
    setCurrentReport,
    setReportTimeRange,
} from '../../actions/apiSetInfo';
import { getUser } from '../../actions/apiGetInfo';
import {
    change_page,
    toggleSidebarRight,
} from '../../actions/toggleMenu';
import { logout } from '../../actions/authentication';
import {
    lookupPermission,
} from '../../actions/lookups';
import './header.scss';

const Header = () => {
    const [modalIsOpen, setModalIsOpen] = useState(false);
    const dispatch = useDispatch();

    const {
        current_user,
        dealer_settings,
        interval_number,
        installation,
    } = useSelector(state => ({
        nav_status: state.settings_nav.menus.nav_status,
        report_menu_status: state.settings_nav.menus.report_menu_status,
        auth: state.current_user.isAuthenticated,
        current_user: state.current_user,
        update_status: state.update_status,
        dealer_settings: state.settings_site.dealer_settings,
        interval_number: state.settings_nav.interval_number,
        installation: state.settings_dealerships.installation,
        softname: state.settings_dealerships.installation.softname || '',
        report_department_data: state.settings_reports.report_department_data,
        departments: state.settings_departments.departments,
        inactive_status: state.settings_nav.inactive_status,
    }), shallowEqual);

    const dealerSettingValue = useCallback((name) => {
        const settings = dealer_settings;
        let value = _.sortBy(
            _.filter(settings, o => o.key === name && o.dealership === installation.subdomain),
            'id'
        );
        if (value.length === 0) {
            value = _.sortBy(
                _.filter(settings, o => o.key === name && !o.dealership),
                'id'
            );
        }
        return value.length > 0 && value[0]?.value ? value[0].value : '';
    }, [dealer_settings, installation.subdomain]);

    const logoutHandler = useCallback(() => {
        dispatch(logout(true));
    }, [dispatch]);

    const changePageHandler = useCallback((page) => {
        if (page === 'dealerships') {
            clearInterval(interval_number);
            dispatch(setDealership(''));
            dispatch(resetRefresh());
            dispatch(resetNav());
            dispatch(resetAdmin());
            dispatch(resetSite());
        } else if (page === 'reports') {
            dispatch(setCurrentReportTab('Reports'));
            dispatch(change_page(page));
        } else {
            dispatch(change_page(page));
        }
    }, [interval_number, dispatch]);

    const showDashboard = current_user.position === 'super_admin'
        || current_user.position === 'admin'
        || lookupPermission('page_dashboard');

    const primaryNav = useMemo(() => {
        const items = [
            <ButtonWithIcon
                name="dealerships"
                key="dealerships"
                label="Dealerships"
                icon="far fa-home"
                onClick={changePageHandler}
            />,
        ];
        if (showDashboard) {
            items.push(
                <ButtonWithIcon
                    name="dashboard"
                    key="dashboard"
                    label="Dashboard"
                    icon="fas fa-tachometer-alt"
                    onClick={changePageHandler}
                />
            );
        }
        items.push(
            <ButtonWithIcon
                name="vehicles"
                key="vehicles"
                label="Vehicles"
                icon="far fa-car"
                onClick={changePageHandler}
            />
        );
        return items;
    }, [showDashboard, changePageHandler]);

    const utilityNav = useMemo(() => {
        const items = [<SupportContact key="support" />];

        if (dealerSettingValue('live_chat') === 'true') {
            items.push(
                <ButtonWithIcon
                    name="chat"
                    key="chat"
                    label="Chat"
                    icon="far fa-comments"
                    onClick={() => dispatch(toggleSidebarRight())}
                />
            );
        }

        if (dealerSettingValue('support-center') === 'true') {
            const supportName = installation.database === 'pilson' ? 'support-videos' : 'support-center';
            items.push(
                <ButtonWithIcon
                    name={supportName}
                    key={supportName}
                    label="Support Center"
                    icon="fas fa-user-headset"
                    onClick={changePageHandler}
                />
            );
        }
        return items;
    }, [
        dealerSettingValue,
        installation.database,
        changePageHandler,
        dispatch,
    ]);

    const headerStyle = {
        backgroundColor: 'white',
        background: 'white',
        color: 'rgb(60,60,60)'
    };

    return (
        <div className="site-header-container">
            <div className="site-header">
                <div className="site-header-left">
                    <div className="site-header-logo">
                        <img className="site" key="toplogo" src={Logo} alt="Logo" />
                    </div>
                    <ul className="site-header-nav-links site-header-primary">
                        {primaryNav}
                    </ul>
                </div>
                <div className="site-header-center">
                    <div className="site-header-reporting">
                        <TimeDashboard />
                    </div>
                </div>
                <div className="site-header-right">
                    <ul className="site-header-nav-links site-header-utilities">
                        <NotificationBell key="notifications" />
                        {utilityNav}
                        <ButtonWithIcon
                            name="logout"
                            key="logout"
                            label="Log Out"
                            icon="far fa-power-off"
                            onClick={logoutHandler}
                        />
                    </ul>
                </div>
            </div>
        </div>
    );
};

export default Header;